import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { analyzeA4EditorialCoverage, getA4DocTypeEditorialProfile } from '@/lib/a4DocTypeEditorialProfiles';

const STATUS_CLASS = {
  'Coberto': 'bg-emerald-100 text-emerald-800',
  'Parcial': 'bg-amber-100 text-amber-900',
  'A rever': 'bg-slate-100 text-slate-700',
};

export const TemplateDocTypeEditorialPanel = ({ previewMeta, selectedLayout, selectedTemplate }) => {
  const profile = getA4DocTypeEditorialProfile(selectedTemplate?.document_type);
  const coverage = analyzeA4EditorialCoverage(selectedLayout, profile);

  if (!profile || !coverage) return null;

  return (
    <Card className="overflow-hidden" data-testid="template-doc-type-editorial-panel">
      <div className="border-b bg-slate-50 px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">Camada editorial por `doc_type` A4</p>
            <p className="text-[11px] text-muted-foreground">{profile.label}: foco editorial em {profile.previewFocus}.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" data-testid="template-doc-type-editorial-badge">{selectedTemplate?.document_type}</Badge>
            <Badge variant="outline" data-testid="template-doc-type-editorial-route-badge">{profile.runtimeRouteFamily}</Badge>
          </div>
        </div>
      </div>

      <div className="grid gap-3 border-b px-4 py-3 md:grid-cols-4" data-testid="template-doc-type-editorial-stats-grid">
        <div className="rounded-xl border bg-white px-3 py-2">
          <Label className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Secções cobertas</Label>
          <p className="mt-1 text-lg font-semibold">{coverage.coveredSections}</p>
        </div>
        <div className="rounded-xl border bg-white px-3 py-2">
          <Label className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Parciais</Label>
          <p className="mt-1 text-lg font-semibold">{coverage.partialSections}</p>
        </div>
        <div className="rounded-xl border bg-white px-3 py-2">
          <Label className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">A rever</Label>
          <p className="mt-1 text-lg font-semibold">{coverage.reviewSections}</p>
        </div>
        <div className="rounded-xl border bg-white px-3 py-2">
          <Label className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Paridade activa</Label>
          <p className="mt-1 text-sm font-semibold">{previewMeta?.renderPipeline === 'render_pdf_runtime_pipeline' ? 'Preview → runtime → PDF' : 'A aguardar preview'}</p>
        </div>
      </div>

      <div className="grid gap-3 px-4 py-4 xl:grid-cols-2" data-testid="template-doc-type-editorial-sections-grid">
        {coverage.sections.map((section) => (
          <div key={section.id} className="rounded-2xl border bg-white p-3" data-testid={`template-doc-type-editorial-section-${section.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold">{section.title}</p>
                <p className="text-[11px] text-muted-foreground">{section.description}</p>
              </div>
              <Badge className={STATUS_CLASS[section.status]} data-testid={`template-doc-type-editorial-status-${section.id}`}>{section.status}</Badge>
            </div>
            <div className="mt-3 space-y-2">
              <div>
                <Label className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Presentes</Label>
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`template-doc-type-editorial-present-${section.id}`}>
                  {section.matched.length > 0 ? section.matched.map((binding) => (
                    <Badge key={`${section.id}-${binding.value}`} variant="outline">{binding.label}</Badge>
                  )) : <span className="text-[11px] text-muted-foreground">Nenhum binding detectado.</span>}
                </div>
              </div>
              <div>
                <Label className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Por rever</Label>
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`template-doc-type-editorial-missing-${section.id}`}>
                  {section.missing.length > 0 ? section.missing.map((binding) => (
                    <Badge key={`${section.id}-missing-${binding.value}`} variant="secondary">{binding.label}</Badge>
                  )) : <span className="text-[11px] text-emerald-700">Secção editorial estável.</span>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};